import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useInviteEmailDesignPreview } from 'features/InviteEmailDesign/useInviteEmailDesignPreview'
import { useLayoutEffect, useRef, useState } from 'react'

import styles from './InviteEmailDesign.module.scss'

const PREVIEW_EMAIL_WIDTH = 500
const THUMB_WIDTH = 88

const InviteEmailDesignTemplatePreview = ({
  templateId,
  enabled = true,
  variant = 'inline',
  fitToContainer = false,
  scaleToWidth = false,
  emptyLabel = 'Preview unavailable',
  refreshKey = 0,
  previewHtml: previewHtmlProp,
  isLoading: isLoadingProp,
  hasError: hasErrorProp
}) => {
  const useInternalPreview = previewHtmlProp === undefined
  const internalPreview = useInviteEmailDesignPreview(templateId, {
    enabled: enabled && useInternalPreview,
    refreshKey
  })
  const previewHtml = useInternalPreview ? internalPreview.previewHtml : previewHtmlProp
  const isLoading = useInternalPreview ? internalPreview.isLoading : Boolean(isLoadingProp)
  const hasError = useInternalPreview ? internalPreview.hasError : Boolean(hasErrorProp)
  const inlineContainerRef = useRef(null)
  const inlineContentRef = useRef(null)
  const cardContainerRef = useRef(null)
  const cardContentRef = useRef(null)
  const [inlineScale, setInlineScale] = useState(1)
  const [inlineLayoutHeight, setInlineLayoutHeight] = useState(null)
  const [cardScale, setCardScale] = useState(0.55)
  const [cardLayoutHeight, setCardLayoutHeight] = useState(120)

  const variantClass =
    variant === 'thumb'
      ? styles.templatePreviewThumb
      : variant === 'card'
      ? styles.templatePreviewCard
      : variant === 'modal'
      ? styles.templatePreviewModal
      : styles.templatePreviewInline

  useLayoutEffect(() => {
    if (variant !== 'card' || !previewHtml) {
      return
    }

    let resizeFrame = 0

    function updateCardScale() {
      const container = cardContainerRef.current
      const content = cardContentRef.current
      if (!container) {
        return
      }

      const naturalHeight = content?.scrollHeight || 0
      const scaleX = container.clientWidth / PREVIEW_EMAIL_WIDTH
      const scaleY =
        fitToContainer && naturalHeight
          ? container.clientHeight / naturalHeight
          : scaleX
      const nextScale = Math.min(scaleX, scaleY, 1)
      const nextLayoutHeight = Math.ceil((naturalHeight || PREVIEW_EMAIL_WIDTH) * nextScale)

      if (nextScale > 0) {
        setCardScale(current => (current === nextScale ? current : nextScale))
        setCardLayoutHeight(current =>
          current === nextLayoutHeight ? current : nextLayoutHeight
        )
      }
    }

    function scheduleCardScaleUpdate() {
      cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(updateCardScale)
    }

    updateCardScale()

    const animationFrame = requestAnimationFrame(updateCardScale)
    const imageLoadTimeout = window.setTimeout(updateCardScale, 120)

    window.addEventListener('resize', scheduleCardScaleUpdate)

    const contentNode = cardContentRef.current
    const images = contentNode ? contentNode.querySelectorAll('img') : []
    images.forEach(image => {
      if (!image.complete) {
        image.addEventListener('load', scheduleCardScaleUpdate)
      }
    })

    return () => {
      cancelAnimationFrame(animationFrame)
      cancelAnimationFrame(resizeFrame)
      window.clearTimeout(imageLoadTimeout)
      window.removeEventListener('resize', scheduleCardScaleUpdate)
      images.forEach(image => {
        image.removeEventListener('load', scheduleCardScaleUpdate)
      })
    }
  }, [fitToContainer, previewHtml, templateId, variant])

  useLayoutEffect(() => {
    if (variant !== 'inline' || !previewHtml) {
      return
    }

    let resizeFrame = 0

    function updateInlineScale() {
      const container = inlineContainerRef.current
      const content = inlineContentRef.current
      if (!container || !content) {
        return
      }

      const naturalHeight = content.scrollHeight
      const naturalWidth = PREVIEW_EMAIL_WIDTH
      if (!naturalHeight) {
        return
      }

      const containerWidth = container.clientWidth
      const availableHeight = fitToContainer
        ? Math.max(container.clientHeight, 160)
        : Math.max(window.innerHeight - container.getBoundingClientRect().top - 24, 240)

      const scaleX = Math.max(containerWidth - 8, 0) / naturalWidth
      const scaleY = availableHeight / naturalHeight
      const nextScale = scaleToWidth
        ? Math.min(Math.max(scaleX, 0.01), 1)
        : Math.min(scaleX, scaleY, 1)
      const nextLayoutHeight = Math.ceil(naturalHeight * nextScale)

      setInlineScale(current => (current === nextScale ? current : nextScale))
      setInlineLayoutHeight(current =>
        current === nextLayoutHeight ? current : nextLayoutHeight
      )
    }

    function scheduleInlineScaleUpdate() {
      cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(updateInlineScale)
    }

    updateInlineScale()

    const animationFrame = requestAnimationFrame(updateInlineScale)
    const imageLoadTimeout = window.setTimeout(updateInlineScale, 120)

    window.addEventListener('resize', scheduleInlineScaleUpdate)

    const contentNode = inlineContentRef.current
    const images = contentNode ? contentNode.querySelectorAll('img') : []
    images.forEach(image => {
      if (!image.complete) {
        image.addEventListener('load', scheduleInlineScaleUpdate)
      }
    })

    return () => {
      cancelAnimationFrame(animationFrame)
      cancelAnimationFrame(resizeFrame)
      window.clearTimeout(imageLoadTimeout)
      window.removeEventListener('resize', scheduleInlineScaleUpdate)
      images.forEach(image => {
        image.removeEventListener('load', scheduleInlineScaleUpdate)
      })
    }
  }, [fitToContainer, previewHtml, scaleToWidth, templateId, variant])

  if (isLoading) {
    return (
      <div className={[variantClass, styles.templatePreviewLoading].join(' ')}>
        <LoadingIndicator />
      </div>
    )
  }

  if (hasError || !previewHtml) {
    return (
      <div className={[variantClass, styles.templatePreviewEmpty].join(' ')}>
        <span>{emptyLabel}</span>
      </div>
    )
  }

  if (variant === 'thumb') {
    const thumbScale = THUMB_WIDTH / PREVIEW_EMAIL_WIDTH

    return (
      <div className={styles.templatePreviewThumb}>
        <div
          className={styles.templatePreviewScaledContent}
          style={{
            width: `${PREVIEW_EMAIL_WIDTH}px`,
            transform: `scale(${thumbScale})`
          }}
        >
          <div
            className={[styles.templatePreviewHtml, 'no-style-reset'].join(' ')}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>
    )
  }

  if (variant === 'card') {
    const scaledWidth = PREVIEW_EMAIL_WIDTH * cardScale

    return (
      <div className={styles.templatePreviewCard} ref={cardContainerRef}>
        <div
          className={styles.templatePreviewCardFrame}
          style={{
            width: `${scaledWidth}px`,
            height: fitToContainer ? `${cardLayoutHeight}px` : 'auto'
          }}
        >
          <div
            ref={cardContentRef}
            className={[styles.templatePreviewCardScaled, 'no-style-reset'].join(' ')}
            style={{
              width: `${PREVIEW_EMAIL_WIDTH}px`,
              transform: `scale(${cardScale})`
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>
    )
  }

  if (variant === 'inline') {
    return (
      <div
        className={[
          styles.templatePreviewInline,
          fitToContainer ? styles.templatePreviewInlineBounded : ''
        ]
          .filter(Boolean)
          .join(' ')}
        ref={inlineContainerRef}
      >
        <div
          className={styles.templatePreviewInlineShell}
          style={{
            width: `${Math.ceil(PREVIEW_EMAIL_WIDTH * inlineScale)}px`,
            height: inlineLayoutHeight ? `${inlineLayoutHeight}px` : 'auto'
          }}
        >
          <div
            className={styles.templatePreviewScaledContent}
            style={{
              width: `${PREVIEW_EMAIL_WIDTH}px`,
              transform: `scale(${inlineScale})`
            }}
          >
            <div
              ref={inlineContentRef}
              className={[styles.templatePreviewHtml, 'no-style-reset'].join(' ')}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={variantClass}>
      <div className={styles.templatePreviewFrame}>
        <div
          className={[styles.templatePreviewHtml, 'no-style-reset'].join(' ')}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      </div>
    </div>
  )
}

export default InviteEmailDesignTemplatePreview
